import { BROWSER } from 'esm-env';
import type { Attachment } from 'svelte/attachments';

export type ScrollActivityCallback = (isScrolling: boolean) => void;
export type ScheduleScrollSettled = (callback: () => void, delayMs: number) => () => void;

/**
 *
 *
 *
 */
const SCROLL_SETTLE_DELAY_MS = 100;

type NarrowEventTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

/**
 *
 *
 *
 */
export class ViewportScrollObserver {
    private callbacks = new Set<ScrollActivityCallback>();
    private cancelSettle: (() => void) | undefined;
    private isScrolling = false;
    private initialized = false;

    constructor(
        private readonly scheduleScrollSettled: ScheduleScrollSettled,
        private readonly injectedEventTarget?: NarrowEventTarget,
    ) {}

    /**
 *
 *
 */
    observe(callback: ScrollActivityCallback): Attachment {
        return () => {
            this.initialize();
            this.callbacks.add(callback);
            callback(this.isScrolling);

            return () => {
                this.callbacks.delete(callback);
                if (this.callbacks.size === 0) this.dispose();
            };
        };
    }

    private initialize() {
        if (this.initialized || !BROWSER) return;

        this.eventTarget.addEventListener('scroll', this.handleScroll, { passive: true });
        this.initialized = true;
    }

    private get eventTarget(): NarrowEventTarget {
        /*                                                  */
        return this.injectedEventTarget ?? window;
    }

    private handleScroll = (): void => {
        if (!this.isScrolling) {
            this.isScrolling = true;
            this.notify();
        }

        this.cancelSettle?.();
        this.cancelSettle = this.scheduleScrollSettled(() => {
            this.cancelSettle = undefined;
            this.isScrolling = false;
            this.notify();
        }, SCROLL_SETTLE_DELAY_MS);
    };

    public dispose(): void {
        this.cancelSettle?.();
        this.cancelSettle = undefined;
        this.isScrolling = false;
        if (this.initialized) this.eventTarget.removeEventListener('scroll', this.handleScroll);
        this.initialized = false;
        this.callbacks.clear();
    }

    private notify(): void {
        for (const callback of this.callbacks) {
            callback(this.isScrolling);
        }
    }
}

export const viewportScrollObserver = new ViewportScrollObserver((callback, delayMs) => {
    const handle = setTimeout(callback, delayMs);
    return () => clearTimeout(handle);
});
